/**
 * driverDepartureTick.js — Gère les préavis de départ chauffeurs
 * (08/02/2026 — Q2 user fix : refus de contre-proposition).
 *
 * Avant ce fix : refus du counter-offer → chauffeur disparaissait
 * silencieusement (filter sur drivers[]). Désormais :
 *
 *   1. Le chauffeur est marqué `pendingDeparture: { noticeUntil,
 *      expectedRaise, ... }` au lieu d'être supprimé.
 *   2. Un email kind='decision' est poussé en inbox avec 2 choix :
 *      « Aligner sur l'attendu » (effet driverAlignSalary) ou
 *      « Acter la démission » (effet driverAcceptDeparture).
 *   3. Si le préavis (30 j) expire sans choix → le chauffeur quitte
 *      automatiquement avec archive des formations (driverFire-like).
 *
 * Ce module fait deux choses, appelé chaque jour après le set() final :
 *   - Pousse les lettres en attente (`_pendingDepartureLetters`).
 *   - Finalise les préavis échus (noticeUntil <= totalDays).
 */

export function _processDepartureNotices(get, set) {
  const state = get();
  const today = state.totalDays || 0;

  // 1. Push pending departure letters (kind='decision' email with 2 choices).
  const pending = state._pendingDepartureLetters || [];
  // 08/02/2026 — Q2 P2 : tooltip pédagogique sur la 1ère négo ratée.
  // On préfixe la 1ère lettre avec un encadré explicatif et on flag pour
  // ne pas le rejouer (set seenFirstRenegoFailure).
  const firstTimeTooltip = !state.seenFirstRenegoFailure && pending.length > 0;
  if (pending.length > 0 && typeof get().pushCustomMessage === 'function') {
    pending.forEach((letter, idx) => {
      const expectedStr = letter.expectedRaise.toLocaleString('fr-FR');
      const isFirst = firstTimeTooltip && idx === 0;
      const tutorialBlock = isFirst
        ? `**📖 Comment ça marche ?** Lorsque vous refusez une demande de hausse `
          + `ou faites une contre-offre rejetée, le chauffeur dépose un préavis `
          + `légal de 30 jours pendant lesquels il/elle continue de travailler. `
          + `Vous avez deux options ci-dessous :\n\n`
          + `   • **Aligner** le salaire sur l'attendu → préavis annulé immédiatement (+15 satisfaction).\n`
          + `   • **Accepter** le départ → le chauffeur quitte au terme du préavis. `
          + `Ses formations et permis sont conservés dans vos archives (« Anciens chauffeurs ») `
          + `pour un éventuel ré-engagement.\n\n`
          + `Aucun choix ? Le chauffeur part automatiquement à expiration du préavis.\n\n`
          + `---\n\n`
        : '';
      get().pushCustomMessage({
        kind: 'decision',
        senderType: 'driver',
        senderName: letter.driverName,
        senderRole: 'Chauffeur',
        tone: 'formal',
        subject: `Préavis de démission — ${letter.driverName}`,
        body: tutorialBlock
          + `Madame, Monsieur,\n\n`
          + `Suite à notre récente discussion sur la revalorisation salariale, `
          + `et la contre-proposition que vous m'avez soumise, je me vois `
          + `contraint(e) de vous notifier mon **préavis de démission**.\n\n`
          + `Conformément à la convention collective des Transports, je `
          + `respecterai un préavis de **${letter.noticeDays} jours** à compter `
          + `de ce jour. Mon dernier jour de présence sera donc fixé `
          + `${letter.noticeDays} jours après réception.\n\n`
          + `Si vous souhaitez **reconsidérer ma demande initiale de `
          + `+${expectedStr} €/mois**, je suis prêt(e) à retirer ce préavis. `
          + `À défaut, je vous prie d'agréer mes salutations distinguées.\n\n`
          + `${letter.driverName}`,
        driverId: letter.driverId,
        ttlDays: letter.noticeDays,
        choices: [
          {
            id: 'align',
            label: `Aligner sur +${expectedStr} €/mois (annule le préavis)`,
            effects: [{ kind: 'driverAlignSalary', amount: letter.expectedRaise }],
          },
          {
            id: 'accept',
            label: `Accepter le départ (préavis ${letter.noticeDays} j)`,
            effects: [{ kind: 'driverAcceptDeparture' }],
          },
        ],
        defaultChoiceId: 'accept',
      });
    });
    const patch = { _pendingDepartureLetters: [] };
    if (firstTimeTooltip) patch.seenFirstRenegoFailure = true;
    set(patch);
  }

  // 2. Auto-finalize expired notices (player did nothing within the
  // preavis window → driver leaves with trainings archived).
  const drivers = state.drivers || [];
  const expired = drivers.filter(
    d => d.pendingDeparture && d.pendingDeparture.noticeUntil <= today
  );
  if (expired.length === 0) return;

  const expiredIds = new Set(expired.map(d => d.id));
  const newArchive = { ...(state.firedDriversArchive || {}) };
  // 23/07/2026 (choix user 3a) — préavis « pression congés » : l'indemnité
  // conventionnelle (~3 mois) est débitée ICI, à l'expiration du préavis.
  let indemnityTotal = 0;
  expired.forEach(d => {
    if (d.pendingDeparture?.reason === 'holiday_pressure') {
      indemnityTotal += d.pendingDeparture.indemnity || 0;
    }
    newArchive[d.dataId] = {
      level: d.level,
      xp: d.xp,
      specialty: d.specialty,
      specialty2: d.specialty2,
      extraSpecialties: d.extraSpecialties || [],
      licenses: d.licenses,
      competence: d.competence,
      accidentRisk: d.accidentRisk,
      employerCost: d.employerCost,
      trainings: d.trainings || [],
      totalTrips: d.totalTrips || 0,
      totalKm: d.totalKm || 0,
      firedAt: today,
    };
  });

  set({
    drivers: drivers.filter(d => !expiredIds.has(d.id)),
    vehicles: (state.vehicles || []).map(v =>
      expiredIds.has(v.assignedDriverId) ? { ...v, assignedDriverId: null } : v
    ),
    firedDriversArchive: newArchive,
    ...(indemnityTotal > 0 ? { treasury: (state.treasury || 0) - indemnityTotal } : {}),
    notifications: [...(state.notifications || []).slice(-30),
      ...expired.map(d => ({
        id: `departure-final-${d.id}-${today}`,
        type: 'driver_departure_final',
        title: `Départ effectif — ${d.name}`,
        message: d.pendingDeparture?.reason === 'holiday_pressure'
          ? `${d.name} a quitté l'entreprise (congés non accordés). Indemnité conventionnelle versée : −${(d.pendingDeparture.indemnity || 0).toLocaleString('fr-FR')} €. Ses formations sont conservées en cas de retour.`
          : `${d.name} a quitté l'entreprise après expiration du préavis. Ses formations sont conservées en cas de retour.`,
        day: today,
        read: false,
      })),
    ],
  });

  if (typeof get().pushCustomMessage === 'function') {
    expired.forEach(d => {
      const isPressure = d.pendingDeparture?.reason === 'holiday_pressure';
      get().pushCustomMessage({
        kind: 'info',
        senderType: 'hr',
        senderName: 'Service RH',
        tone: 'formal',
        subject: `Sortie effective — ${d.name}`,
        body: isPressure
          ? `Le préavis de ${d.name} (congés non accordés) est arrivé à son terme sans attribution de congés. `
            + `Rupture négociée actée : indemnité de ${(d.pendingDeparture.indemnity || 0).toLocaleString('fr-FR')} € versée. `
            + `Ses formations et permis sont conservés dans nos archives pour un éventuel retour.`
          : `Le préavis de ${d.name} est arrivé à son terme aujourd'hui sans alignement de salaire. `
            + `Son contrat est rompu. Ses formations et permis acquis sont conservés dans nos archives — `
            + `il/elle pourra revenir sur le marché de l'emploi avec ces qualifications préservées.`,
        ttlDays: 14,
      });
    });
  }
}
